import { Controller } from "@hotwired/stimulus"

// Preserves the user's infinite scroll position on the products index.
//
// As the user scrolls, lazy turbo frames (products_page_2, products_page_3, ...)
// load more products, but that state only exists in the DOM. When the index is
// reloaded from the network (e.g. Turbo's snapshot cache was cleared by a form
// submission deeper in the navigation stack), the user would be dumped back at
// page 1.
//
// This controller records the deepest loaded page, the tapped product, and the
// scroll offset in a cookie. ProductsController#index reads that cookie on the
// next full render and renders all previously loaded pages in a single
// response, then this controller restores the exact scroll position.
export default class extends Controller {
  static values = {
    pages: { type: Number, default: 1 },
    anchorId: String,
    scrollY: { type: Number, default: 0 }
  }

  connect() {
    this.deepestPage = Math.max(this.pagesValue, this.deepestLoadedPage())
    this.restoreScrollPosition()
  }

  disconnect() {
    this.stopHoldingScrollPosition()
  }

  // Bound via data-action: turbo:frame-load
  frameLoaded(event) {
    const frameId = event.target.id

    // The base frame reloads when the user changes filters or search, which
    // starts a fresh list back at page 1
    if (frameId === "products") {
      this.deepestPage = 1
      return
    }

    const page = this.pageNumberFromFrameId(frameId)
    if (page) {
      this.deepestPage = Math.max(this.deepestPage, page)
    }
  }

  // Bound via data-action: click. Captures taps on any product card so the
  // state is saved right before navigating away.
  saveState(event) {
    const card = event.target.closest("[data-product-card-id]")
    if (!card) return

    const state = {
      key: window.location.pathname + window.location.search,
      pages: this.deepestPage,
      anchor: card.dataset.productCardId,
      scrollY: Math.round(window.scrollY)
    }

    document.cookie = `products_index_restore=${encodeURIComponent(JSON.stringify(state))}; path=/products; max-age=1800; SameSite=Lax`
  }

  restoreScrollPosition() {
    const anchorId = this.hasAnchorIdValue ? this.anchorIdValue : ""
    const scrollY = this.scrollYValue

    if (anchorId === "" && scrollY <= 0) return

    // Drop the values so snapshot restores of this page don't re-trigger the scroll
    this.element.removeAttribute(`data-${this.identifier}-anchor-id-value`)
    this.element.removeAttribute(`data-${this.identifier}-scroll-y-value`)

    // Turbo applies its own scroll when the visit finishes rendering, after this
    // controller connects, which would override the scroll below. With live
    // restoration data that happens to land in the right place, but when the
    // WebView was cold booted (e.g. native pull-to-refresh) the restoration data
    // is gone and Turbo scrolls to the top. Mark the visit as already scrolled
    // so Turbo's scroll pass is a no-op either way.
    const currentVisit = window.Turbo?.navigator?.currentVisit
    if (currentVisit) {
      currentVisit.scrolled = true
    }

    // Restore the exact offset the user left at. The server re-renders the same
    // list, so the saved offset lands where they were. Fall back to centering
    // the tapped product when no offset was recorded.
    if (scrollY > 0) {
      window.scrollTo(0, scrollY)
      this.holdScrollPosition(scrollY)
      return
    }

    const card = this.element.querySelector(`[data-product-card-id="${anchorId}"]`)
    if (card) {
      card.scrollIntoView({ behavior: "instant", block: "center" })
    }
  }

  // Keep re-asserting the restored offset for a short window. Android's WebView
  // clamps the scroll when the restored list hasn't finished laying out yet, and
  // can reset it while being re-attached to the destination fragment, both of
  // which happen shortly after this controller connects. Re-scrolling to an
  // offset the page is already at is a visual no-op, so repeating is safe.
  // Stops as soon as the user interacts.
  holdScrollPosition(targetY) {
    this.stopHoldingScrollPosition()

    this.holdDeadline = performance.now() + 600

    this.extendHoldOnLoad = () => {
      this.holdDeadline = performance.now() + 600
    }
    document.addEventListener("turbo:load", this.extendHoldOnLoad, { once: true })

    this.stopHold = () => this.stopHoldingScrollPosition()
    window.addEventListener("touchstart", this.stopHold, { passive: true })
    window.addEventListener("wheel", this.stopHold, { passive: true })

    const tick = () => {
      if (performance.now() > this.holdDeadline) {
        this.stopHoldingScrollPosition()
        return
      }

      if (Math.abs(window.scrollY - targetY) > 1) {
        window.scrollTo(0, targetY)
      }

      this.holdFrame = requestAnimationFrame(tick)
    }
    this.holdFrame = requestAnimationFrame(tick)
  }

  stopHoldingScrollPosition() {
    if (this.holdFrame) {
      cancelAnimationFrame(this.holdFrame)
      this.holdFrame = null
    }

    if (this.extendHoldOnLoad) {
      document.removeEventListener("turbo:load", this.extendHoldOnLoad)
      this.extendHoldOnLoad = null
    }

    if (this.stopHold) {
      window.removeEventListener("touchstart", this.stopHold)
      window.removeEventListener("wheel", this.stopHold)
      this.stopHold = null
    }
  }

  deepestLoadedPage() {
    let deepest = 1

    this.element.querySelectorAll('turbo-frame[id^="products_page_"][complete]').forEach((frame) => {
      const page = this.pageNumberFromFrameId(frame.id)
      if (page) {
        deepest = Math.max(deepest, page)
      }
    })

    return deepest
  }

  pageNumberFromFrameId(frameId) {
    const match = frameId.match(/^products_page_(\d+)$/)
    return match ? parseInt(match[1], 10) : null
  }
}
